// Import necessary dependencies and components
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MessageCircle, Briefcase, Users } from 'lucide-react';
import { HeroSection } from '../components/home/HeroSection'; // Main hero section with search functionality
import { FeatureCard } from '../components/home/FeatureCard'; // Component to display features
import { StepCard } from '../components/home/StepCard'; // Component to display steps
import { LatestJobs } from '../components/home/LatestJobs'; // Section displaying the latest job listings
import { getWhatsAppLink } from '../lib/utils'; // Utility to generate WhatsApp links

// Interfaces for features and steps
interface Feature {
  icon: LucideIcon;
  title: string;
  description: string;
}

interface Step {
  number: number;
  title: string;
  description: string;
  link: string;
  isExternal: boolean; // Indicates if the link is external
}

// Main Index component for the homepage
export function Index() {
  const [searchTitle, setSearchTitle] = useState(''); // Job title search state
  const [searchLocation, setSearchLocation] = useState(''); // Job location search state
  const navigate = useNavigate(); // Hook to navigate between routes

  // Handle search action
  const handleSearch = () => {
    navigate('/jobs', {
      state: { title: searchTitle, location: searchLocation }, // Pass search parameters to the jobs page
    });
  };

  // Features data
  const features: Feature[] = [
    {
      icon: MessageCircle,
      title: 'Assistant WhatsApp Intelligent',
      description:
        "Notre bot WhatsApp vous guide 24/7 dans votre recherche d'emploi avec des réponses personnalisées.",
    },
    {
      icon: Briefcase,
      title: 'Offres Personnalisées',
      description:
        'Recevez des offres d\'emploi sur mesure selon votre profil et vos préférences professionnelles.',
    },
    {
      icon: Users,
      title: 'Profil Transmis au Recruteur',
      description:
        'Vos informations ainsi que votre CV seront transmises au recruteur pour une meilleure visibilité.',
    },
  ];

  // Steps data
  const steps: Step[] = [
    {
      number: 1,
      title: 'Créez votre profil',
      description:
        'Inscrivez-vous simplement sur WhatsApp et créez votre profil en quelques minutes.',
      link: getWhatsAppLink(), // External WhatsApp redirection
      isExternal: true,
    },
    {
      number: 2,
      title: 'Découvrez les offres',
      description:
        'Explorez des offres personnalisées correspondant à vos critères de recherche.',
      link: '/jobs', // Internal link to the jobs page
    },
    {
      number: 3,
      title: 'Postulez rapidement',
      description:
        'Envoyez votre candidature en un clic et suivez son évolution en temps réel.',
      link: '/jobs', // Internal link to the jobs page
    },
  ];

  return (
    <div className="min-h-screen bg-background-light">
      {/* Hero Section with search */}
      <HeroSection
        searchTitle={searchTitle} // Current job title search
        searchLocation={searchLocation} // Current location search
        onSearchTitleChange={setSearchTitle} // Update job title search
        onSearchLocationChange={setSearchLocation} // Update location search
        onSearch={handleSearch} // Trigger search
      />

      {/* Why Choose Us Section */}
      <section className="py-20 bg-primary-lightest">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="section-title text-4xl">Pourquoi nous choisir ?</h2>
            <p className="section-subtitle">
              Des outils innovants pour simplifier votre recherche d'emploi
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <FeatureCard key={index} {...feature} />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="section-title text-4xl">Comment ça marche ?</h2>
            <p className="section-subtitle">
              Trouvez votre prochain emploi en trois étapes simples
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step) => (
              <StepCard
                key={step.number}
                number={step.number}
                title={step.title}
                description={step.description}
                link={step.link}
                isExternal={step.isExternal}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Latest Jobs Section */}
      <section className="py-20 bg-primary-lightest">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="section-title text-4xl">Dernières Offres</h2>
            <p className="section-subtitle">
              Découvrez les opportunités les plus récentes
            </p>
          </div>
          <LatestJobs /> {/* Displays the latest job postings */}
        </div>
      </section>

      {/* Call-to-Action Section */}
      <section className="bg-secondary py-20">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-primary-dark mb-6">
            Prêt à commencer votre recherche ?
          </h2>
          <p className="text-lg text-primary-light mb-8 max-w-2xl mx-auto">
            Rejoignez notre plateforme et accédez à des milliers d'offres
            d'emploi dans le secteur du paysagisme.
          </p>
          <button
            onClick={() => navigate('/jobs')} // Navigate to the jobs page
            className="btn-primary text-lg"
          >
            Explorer les offres
          </button>
        </div>
      </section>
    </div>
  );
}
